/**
 * Bringing one table's rows in from a file.
 *
 * An import is not "read a file, write rows". Between the file and the table
 * sits a correspondence nobody has established yet - which column is which
 * field, which row is which record, what to do when two rows claim to be the
 * same one - and settling that is the whole of the work. So this is a
 * negotiation that converges on a Plan, and then the Plan is carried out.
 *
 * Three screens, one per uncertainty: agree the columns, read what that plan
 * would do, then see what it did and take back what should not have.
 *
 * One dialog serves Customers and Leads. Everything either of them has that the
 * other does not arrives in the schema, and nothing here asks which table it is
 * looking at - the table is a value it carries to the service, never a branch.
 * Two entities in two components is how the first Lead importer grew to 3,600
 * lines that Customers could not use one of.
 */
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TimeoutError, timeout } from 'rxjs';

import {
  PlanDecisions,
  TableApplyResult,
  TableImportExportService,
  TransferPlan,
  TransferSchema,
  TransferTable,
} from '../../services/table-import-export.service';
import { ImportJobWatcher } from './import-job-watch';
import {
  FileKind,
  openTextFile,
  saveTextFile,
} from '../../shared/file-picker.util';
import {
  loadedMappingNotice,
  mappingFileJson,
  mappingFileName,
  readMappingFile,
} from './mapping-file';
import { RowDecisions } from './plan-decisions';
import { MappingRow, mappingRows, requiredWithoutColumn } from './mapping-rows';

export interface TableImportDialogData {
  table: TransferTable;
  /** What the entity is called on screen. Nothing branches on it. */
  title?: string;
}

type Step = 'file' | 'mapping' | 'plan' | 'result';

/**
 * HOW THIS DIALOG MUST BE OPENED, wherever it is opened from.
 *
 * A Material dialog is dismissible by default, and every default here is
 * wrong for this one. What it holds is not a question with an answer, it is
 * twenty columns somebody has been settling for several minutes, and there is
 * no draft of it anywhere: whatever closes this window throws all of it away
 * without asking.
 *
 *   disableClose        - Escape, and a click on the backdrop. The dialog is
 *                         wide, so "outside it" is a narrow margin next to
 *                         what you are reading. Cancel is on screen the whole
 *                         time and says what it does.
 *   closeOnNavigation   - the one that closes it with nobody touching it.
 *                         Material closes dialogs on ANY navigation, and the
 *                         page underneath is live: a poll's 401 puts up the
 *                         re-login prompt, and the return trip it makes
 *                         afterwards took this window with it.
 *
 * It lives here rather than at each caller because it is a fact about the
 * dialog, not about the page that opened it - Leads and Customers open the
 * same window and must not be able to disagree about this.
 */
export const tableImportDialogOptions:
  Pick<MatDialogConfig, 'autoFocus' | 'disableClose' | 'closeOnNavigation'> = {
  autoFocus: false,
  disableClose: true,
  closeOnNavigation: false,
};

/** What the picker filters on, and what it calls this in "save as type". */
const MappingFileKind: FileKind = {
  description: 'BestRoute column mapping',
  mime: 'application/json',
  extensions: ['.json'],
};

@Component({
  selector: 'app-table-import-dialog',
  templateUrl: './table-import-dialog.component.html',
  styleUrls: ['./table-import-dialog.component.css'],
})
export class TableImportDialogComponent implements OnInit, OnDestroy {
  step: Step = 'file';
  schema: TransferSchema | null = null;
  plan: TransferPlan | null = null;
  result: TableApplyResult | null = null;

  busy = false;
  undoing = false;
  error: string | null = null;

  /** The whole agreed mapping once screen 1 has been touched, field to column. */
  mappingChoice: Record<string, string> = {};
  /** A value for a field no column feeds, put on every row. Posted beside the
   *  field-to-column map, because they are two halves of one answer. */
  mappingConstants: Record<string, string> = {};
  mappingTouched = false;
  rememberMapping = false;
  decisions: RowDecisions = { rowActions: {}, reasonActions: {} };

  private file: File | null = null;
  /** The mapping the plan in hand was built from, to know when to re-stage. */
  private stagedWith = '';
  private changedAnything = false;

  constructor(
    private readonly transfer: TableImportExportService,
    private readonly dialogRef: MatDialogRef<TableImportDialogComponent, boolean>,
    private readonly http: HttpClient,
    @Inject(MAT_DIALOG_DATA) readonly data: TableImportDialogData,
  ) {}

  get table(): TransferTable { return this.data.table; }

  get title(): string { return this.data.title ?? this.data.table; }

  get fileName(): string { return this.file?.name ?? ''; }

  /** What the picker offers, from the table's own list rather than a second
   *  copy of it kept in the template. */
  get accepted(): string {
    return (this.schema?.acceptedExtensions ?? []).join(',');
  }

  /**
   * WHY IT WAS REFUSED, in the server's own words where it gave any.
   *
   * Every refusal used to arrive as one sentence of ours - "We could not read
   * that file" - whatever had actually happened, and that sentence is wrong
   * for most of what can happen: a sign-in that ended, a file type this table
   * does not take, a mapping that would not parse. It sends somebody to
   * inspect a file that is fine, where they will find nothing and lose the
   * time; and it is why a failed import could not be diagnosed from the
   * screen at all. The API answers a refusal with `{ error }`, so the reason
   * was there the whole time and was simply being dropped.
   */
  private static refusedBecause(failure: unknown, fallback: string): string {
    const status = (failure as { status?: unknown } | null)?.status;
    // Neither of these has a body worth reading, and both are wrong to
    // describe as a problem with the file.
    if (status === 0) return 'The browser could not reach the server.';
    if (status === 401) return 'Your sign-in ended before this finished.';

    const body = (failure as { error?: unknown } | null)?.error;
    const said = typeof body === 'string'
      ? body
      : (body as { error?: unknown } | null)?.error;
    return typeof said === 'string' && said.trim() ? said.trim() : fallback;
  }

  ngOnInit(): void {
    this.transfer.getSchema(this.table).subscribe({
      next: schema => this.schema = schema,
      error: () => this.error =
        'We could not read this table’s columns, so there is nothing to'
        + ' match a file against yet.',
    });
  }

  chooseFile(file: File | null): void {
    if (!file) return;
    this.file = file;
    this.stage();
  }

  chooseMapping(choice: Record<string, string>): void {
    this.mappingChoice = choice;
    this.mappingTouched = true;
  }

  chooseConstants(constants: Record<string, string>): void {
    this.mappingConstants = constants;
    this.mappingTouched = true;
  }

  /** What the Save/Load pair has to say for itself, cleared on every change
   *  so a stale "Saved" cannot sit over a mapping that has moved on. */
  mappingNotice: string | null = null;
  /** A picker is open, or a file is being read. */
  savingMapping = false;

  /**
   * KEEPS THIS MAPPING AS A FILE, in a folder the person picks.
   *
   * It was kept on the account until 2026-09-02, under a name typed into the
   * screen. That is a real feature - the next person to import a file of the
   * same shape was offered it - and it was invisible: a Save button, a name
   * box, and no word anywhere about where any of it went, so the reasonable
   * reading was that a file had been written somewhere and lost. The user's
   * ruling is that a thing you cannot see is worse than a thing you have to
   * carry, and a file is a thing you can also send to somebody.
   *
   * Its own button rather than only the Remember tick, because that one saves
   * on Apply: somebody who settles twenty columns and then decides not to
   * import today loses the work.
   */
  async saveMapping(): Promise<void> {
    if (!this.plan || this.savingMapping) return;
    this.savingMapping = true;
    this.mappingNotice = null;
    try {
      const outcome = await saveTextFile(
        mappingFileJson(this.table, this.effectiveMapping, this.mappingConstants),
        mappingFileName(this.table, this.fileName),
        MappingFileKind);
      // A picker somebody closed is not a failure and has nothing to report.
      if (!outcome.done) return;
      this.mappingNotice = outcome.asked
        ? `Saved as ${outcome.name}.`
        : `Saved to your downloads as ${outcome.name} \u2014 this browser does`
          + ' not let a page ask where to put it.';
    } finally {
      this.savingMapping = false;
    }
  }

  /**
   * Puts a saved mapping back over the one on screen.
   *
   * A file carries no promise that it fits. The account-side version was
   * looked up BY the file's own header signature, so whatever came back had
   * been written for a file like this one; nothing guarantees that about a
   * file on disk, which can be for another table or for a spreadsheet whose
   * columns have been renamed since. So what the lookup used to guarantee is
   * checked here, and what does not fit is left out and counted rather than
   * posted as a mapping to a column that is not there.
   */
  async loadMapping(): Promise<void> {
    if (!this.plan || !this.schema || this.savingMapping) return;
    this.savingMapping = true;
    this.mappingNotice = null;
    try {
      const picked = await openTextFile(MappingFileKind);
      if (!picked) return;
      const read = readMappingFile(
        picked.text, this.schema, this.plan.mapping.headers ?? []);
      if (!read.ok) {
        // Nothing on screen is touched by a file we would not use.
        this.mappingNotice = read.reason;
        return;
      }
      this.mappingChoice = read.fields;
      this.mappingConstants = read.constants;
      this.mappingTouched = true;
      this.mappingNotice = loadedMappingNotice(picked.name, read);
    } finally {
      this.savingMapping = false;
    }
  }

  /**
   * The mapping as it stands, whether or not anybody has touched it.
   *
   * Saving before touching anything has to keep what the MATCHER worked out -
   * that is the common case, and `mappingChoice` is empty until the first
   * hand-made change.
   */
  private get effectiveMapping(): Record<string, string> {
    if (this.mappingTouched) return this.mappingChoice;
    return this.plan ? mappingOf(this.plan) : {};
  }

  /**
   * Required fields still reading from no column. Not a warning to click past:
   * each one fails every row in the file for the same reason, so a plan built
   * now would be entirely red and say nothing the person could act on.
   *
   * Which fields these are is the schema's answer, never this dialog's. The
   * entity says what it cannot do without.
   */
  get unmatchedRequired(): MappingRow[] {
    if (!this.plan || !this.schema) return [];
    return requiredWithoutColumn(mappingRows(
      this.schema, this.plan.mapping, this.mappingChoice, this.mappingTouched))
      // A field somebody typed a value for is answered. It reads from no
      // column and never will, and blocking on that would refuse an import
      // over a question the person has already answered on this screen.
      .filter(row => (this.mappingConstants[row.field] ?? '').trim() === '');
  }

  get canLeaveMapping(): boolean {
    return !this.busy && this.unmatchedRequired.length === 0;
  }

  /**
   * A different mapping is a different plan: every row's verdict was reached by
   * reading the columns the way the old mapping said. Re-reading is the only
   * honest way to show what the new one means - and not re-reading when nothing
   * changed is what keeps the second visit to this screen free.
   */
  continueFromMapping(): void {
    // Guarded here rather than only on the button: a disabled button is how
    // this is said, not how it is enforced.
    if (!this.canLeaveMapping) return;
    if (!this.mappingTouched || this.mappingKey() === this.stagedWith) {
      this.step = 'plan';
      return;
    }
    this.stage();
  }

  backToMapping(): void { this.step = 'mapping'; }

  /**
   * Hand the apply to a job and watch it.
   *
   * It used to be written inside this request. nginx closes a proxied request
   * after sixty seconds and a large file takes far longer, so the browser was
   * shown "Nothing was written" while the server went on writing - 1,043 rows
   * of a ten-thousand-row file landed under that message. The job survives the
   * connection; the watch only reports it.
   */
  apply(): void {
    const plan = this.plan;
    if (!plan || this.busy) return;
    this.busy = true;
    this.error = null;
    this.applyDone = 0;
    this.applyTotal = plan.rows.length;

    this.transfer.applyJob(this.table, plan.planId, this.asDecisions())
      .subscribe({
        next: started => this.watchApply(started.jobId),
        // A refusal to START is safe to report plainly: no job exists, so
        // nothing was written. That is not true of a silence once one has.
        error: failure => {
          this.busy = false;
          this.error = 'Nothing was written. '
            + TableImportDialogComponent.refusedBecause(
              failure, 'The import could not be started.')
            + ' Your answers are still here, so you can try again.';
        },
      });
  }

  private watchApply(jobId: string): void {
    this.applyJobId = jobId;
    this.watcher = new ImportJobWatcher(this.http, {
      progress: (done, total) => {
        this.applyDone = done;
        if (total !== null) this.applyTotal = total;
      },
      done: resultJson => {
        this.busy = false;
        this.applyJobId = null;
        this.result = resultJson
          ? JSON.parse(resultJson) as TableApplyResult
          : null;
        this.changedAnything = true;
        this.step = 'result';
      },
      stopped: message => {
        this.busy = false;
        this.applyJobId = null;
        // Rows may well have landed before it stopped, so this does not claim
        // otherwise - the mistake this whole change exists to undo.
        this.error = message
          + ' Some rows may already have been written; reload the table'
          + ' before trying again.';
      },
      gaveUp: message => { this.busy = false; this.error = message; },
      lost: message => { this.busy = false; this.error = message; },
    });
    this.watcher.watch(jobId);
  }

  /** Rows written so far, and how many there are, for the bar. */
  applyDone = 0;
  applyTotal = 0;
  applyJobId: string | null = null;
  private watcher: ImportJobWatcher | null = null;

  /** Nothing to show a percentage of until the job says how many there are. */
  get applyPercent(): number {
    if (this.applyTotal <= 0) return 0;
    return Math.min(100, Math.round((this.applyDone / this.applyTotal) * 100));
  }

  /** The watch stops with the dialog. The JOB does not - it is writing the
   *  file whether or not this screen is open, which is the point of it. */
  ngOnDestroy(): void { this.watcher?.cancel(); }

  undo(): void {
    const result = this.result;
    if (!result || this.undoing) return;
    this.undoing = true;
    this.error = null;
    this.transfer.undo(this.table, result.planId).subscribe({
      next: undone => {
        this.undoing = false;
        this.result = undone;
        this.changedAnything = true;
      },
      error: () => {
        this.undoing = false;
        this.error = 'The import could not be undone. What it wrote is still'
          + ' there.';
      },
    });
  }

  close(): void { this.dialogRef.close(this.changedAnything); }

  /**
   * How long we wait before saying nobody answered.
   *
   * HttpClient has no timeout of its own, and a request that hangs - a dead
   * proxy, a backend that stopped answering - never reaches either callback.
   * The screen then says "Reading the file..." for ever, and because `busy`
   * disables the buttons the person cannot even choose another file. A wait
   * that ends is the difference between a slow import and a dead screen.
   */
  private static readonly NoAnswerAfterMs = 120_000;

  private stage(): void {
    const file = this.file;
    if (!file || this.busy) return;
    this.busy = true;
    this.error = null;
    const settled = this.mappingTouched;
    const mapping = settled
      ? { fields: this.mappingChoice, constants: this.mappingConstants }
      : undefined;
    this.transfer.stage(this.table, file, mapping)
      .pipe(timeout(TableImportDialogComponent.NoAnswerAfterMs))
      .subscribe({
        next: plan => {
          this.busy = false;
          this.plan = plan;
          this.stagedWith = keyOf(mappingOf(plan)) + '|' + keyOf(this.mappingConstants);
          // A re-read for a mapping the person settled is them past that
          // question; a first read leaves them on it.
          this.step = settled ? 'plan' : 'mapping';
        },
        // No answer and a bad file are different problems with different
        // fixes. Telling somebody their file could not be read sends them to
        // inspect a file that is fine, and they will not find anything,
        // because what failed was the trip and not the file.
        error: failure => {
          this.busy = false;
          this.error = failure instanceof TimeoutError
            ? 'The server did not answer. Nothing has been written, so the'
              + ' same file can be sent again once it responds.'
            : TableImportDialogComponent.refusedBecause(
              failure, 'We could not read that file.')
              + ' Nothing has been written.';
          this.step = 'file';
        },
      });
  }

  private asDecisions(): PlanDecisions {
    return {
      // NEVER THE MAPPING. Applying carries out a plan that has already been
      // built, and the plan holds the values that reading the file through
      // this mapping produced - the file itself is gone by then. The server
      // refuses a mapping here rather than silently ignoring one, and it is
      // right to: a caller who sends one believes it will be honoured.
      //
      // This sent it whenever the person had touched the mapping screen,
      // which is the ordinary case, so Apply refused every import where
      // anybody had chosen a column. Re-reading the file is what `stage` is
      // for, and the dialog already re-stages before it gets here.
      mapping: null,
      rowActions: Object.keys(this.decisions.rowActions).length
        ? this.decisions.rowActions
        : null,
      reasonActions: Object.keys(this.decisions.reasonActions).length
        ? this.decisions.reasonActions
        : null,
      rememberMapping: this.rememberMapping,
    };
  }

  /**
   * WHAT MAKES THIS A DIFFERENT READING OF THE FILE.
   *
   * Both halves, because both change what every row means. It was the column
   * map alone, and a constant does not touch that map - so typing "FL" for a
   * field no column feeds left the key identical, the dialog concluded nothing
   * had changed, and it walked to the plan without re-reading. The value
   * reached the screen, the screen said "FL on every row", and the server was
   * never told. What a person had typed was visible everywhere except in the
   * rows they were about to write.
   */
  private mappingKey(): string {
    return keyOf(this.mappingChoice) + '|' + keyOf(this.mappingConstants);
  }
}

/** The mapping a plan was actually built from, in the same shape as a choice. */
function mappingOf(plan: TransferPlan): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const entry of plan.mapping.entries) {
    if (entry.header !== null) fields[entry.field] = entry.header;
  }
  return fields;
}

/** A stable rendering of one mapping, so two of them can be compared. */
function keyOf(fields: Record<string, string>): string {
  return Object.entries(fields)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([field, header]) => `${field}=${header}`)
    .join(' ');
}
